import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../utils/api';
import Button from '../components/Button';
import { useAuth } from '../context/AuthContext';

const ProfilePage: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [updateSuccess, setUpdateSuccess] = useState(false);
  const [updateError, setUpdateError] = useState<string | null>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setUpdateSuccess(false);
    setUpdateError(null);

    try {
      // Validate passwords
      if (!formData.currentPassword) {
        setUpdateError('Du skal angive dit nuværende kodeord.');
        return;
      }
      if (!formData.newPassword) {
        setUpdateError('Du skal angive et nyt kodeord.');
        return;
      }
      if (formData.newPassword !== formData.confirmPassword) {
        setUpdateError('De nye kodeord matcher ikke.');
        return;
      }
      if (formData.newPassword.length < 6) {
        setUpdateError('Det nye kodeord skal være mindst 6 tegn.');
        return;
      }

      // Update password
      await authAPI.changePassword(formData.currentPassword, formData.newPassword);

      setUpdateSuccess(true);
      // Clear password fields
      setFormData({
        currentPassword: '',
        newPassword: '',
        confirmPassword: '',
      });
    } catch (err: any) {
      console.error('Error updating password:', err);
      setUpdateError(err.response?.data?.message || 'Der opstod en fejl ved opdatering af dit kodeord.');
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="bg-snudefryd-beige min-h-screen py-12">
      <div className="container-custom max-w-4xl">
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          {/* Profile Header */}
          <div className="bg-snudefryd-blue p-6 text-white">
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center text-snudefryd-blue text-2xl font-bold">
                {user?.username ? user.username.charAt(0).toUpperCase() : 'A'}
              </div>
              <div>
                <h1 className="text-2xl font-bold">{user?.username || 'Admin'}</h1>
              </div>
            </div>
          </div>

          <div className="p-6">
            <div>
              <h2 className="text-xl font-bold mb-6">Skift kodeord</h2>
              
              <form onSubmit={handleUpdatePassword} className="space-y-6">
                {updateSuccess && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700 mb-4">
                    Dit kodeord er blevet opdateret.
                  </div>
                )}
                
                {updateError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
                    {updateError}
                  </div>
                )}
                
                <div className="space-y-4">
                  <div>
                    <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Nuværende kodeord
                    </label>
                    <input
                      type="password"
                      id="currentPassword"
                      name="currentPassword"
                      value={formData.currentPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Nyt kodeord
                    </label>
                    <input
                      type="password"
                      id="newPassword"
                      name="newPassword"
                      value={formData.newPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                      Bekræft nyt kodeord
                    </label>
                    <input
                      type="password"
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-snudefryd-blue focus:border-snudefryd-blue"
                    />
                  </div>
                </div>
                
                <div className="flex justify-between items-center pt-4">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleLogout}
                  >
                    Log ud
                  </Button>
                  
                  <Button type="submit" variant="primary">
                    Opdater kodeord
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage; 